import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    TemplateRef
} from '@angular/core';
import {ComRestrictionsGridLayoutService} from '@irb/irb-editors/editor-portfolio/com-restrictions/com-restrictions.grid-layout.service';
import {
    IAssetCustomCategories,
    IAssetCustomCategory,
    ICustomSecurity,
    IPortfolioRestrictions,
    ISecurities,
    IPortfolioDetail
} from '@irb/irb-editors/irb-editors.model';
import {CalltoactionService} from '@irb/irb-common/components/calltoaction/calltoaction.service';
import {IrbEditorsService} from '@irb/irb-editors/irb-editors.service';
import {UtilityService} from "@irb-common/services/utility.service";
import {IEvent, IEventSubscriber, IrbEditorsEvents, IrbModelsEvents, IrbSettingsEvents} from '@irb/irb-common/events';
import {ReplaySubject, Subscription} from 'rxjs';
import {AppService} from '@irb/app.service';
import {takeUntil} from 'rxjs/operators';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {DropdownSearchMultipleComponent} from "@irb-common/components/dropdown-search-multiple/dropdown-search-multiple.component";
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import { IGlobalSettings } from "@irb/irb-settings/irb-settings.model";

@Component({ standalone: false,
    selector: 'irb-com-restrictions',
    templateUrl: './com-restrictions.component.html',
    styleUrls: ['./com-restrictions.component.scss', '../../editor-sidebar/editor-sidebar.component.scss']
})
export class ComRestrictionsComponent implements OnInit, IEventSubscriber, OnDestroy {
    @ViewChild('comRestrictionsDeleteOverlay') comRestrictionsDeleteOverlay: TemplateRef<any>;
    @ViewChild('securityRestrictionTooltip') securityRestrictionTooltip: ElementRef;
    @ViewChild('categoryRestrictionTooltip') categoryRestrictionTooltip: ElementRef;
    @ViewChild('securityRestrictionsDropdown') securityRestrictionsDropdown: any;
    @ViewChild('categoryRestrictionsDropdown') categoryRestrictionsDropdown: DropdownSearchMultipleComponent;

    @Input() data: IPortfolioDetail;
    @Input() globalSettings: IGlobalSettings;
    @Input() isValid: boolean;
    @Output() isValidChange = new EventEmitter<boolean>();
    @Output() change = new EventEmitter<any>();

    _data: Array<IPortfolioRestrictions>;
    _securityRowCount: number;
    _categoryRowCount: number;
    _addRestriction: number;
    _securities: ISecurities;
    _grid: any;
    _gridOptions: any;
    _portFolioRestriction: IPortfolioRestrictions = {} as IPortfolioRestrictions;
    valSecDupMessage: string = "";
    valCatDupMessage: string = "";
    _valCatFlag: boolean = false;
    _valSecFlag: boolean = false;
    _categories: IAssetCustomCategories;
    _category: IAssetCustomCategory;
    _selectedCategoryRestriction: IAssetCustomCategory;
    _selectedSecurityRestriction: ICustomSecurity;
    checkCategoryRestriction: boolean;
    checkSecurityRestriction: boolean;
    deleteTarget: any;
    _securitiesDropDown: Array<IDropdownItem>;
    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    eventSubscription: Subscription;

    addNewCategoryItem: any = {
        button: 'Add new category',
        type: 'assetCategory',
        fromComp: 'ComRestrictionsComponent'
    };

    addNewSecurityItem: any = {
        button: 'Add new security',
        type: 'security',
        fromComp: 'ComRestrictionsComponent'
    };
    
    isUiTest = false;
    frameworkComponents: any;
    
    ngOnInit(): void {
        this.eventSubscription = this.as.getEvents().pipe(takeUntil(this.destroyed$)).subscribe((s) => {
            this.processEvent(s);
        });
    }

    processEvent(event: IEvent) {
        switch (event.type) {
            case IrbEditorsEvents.PF_RESTRICTIONS_REFRESH:
                if (event.data) {
                    this.data = event.data.data;
                    this.load();
                }
            case IrbSettingsEvents.ASSET_CATEGORY_UPDATED:
                if (event.data) {
                    if (event.data.type === 'assetCategory' && event.data.fromComp === 'ComRestrictionsComponent') {
                        this.resetCategorySearch(event.data)
                    }
                }
                break;
            case IrbModelsEvents.SAVE_CREATE_NEW_SECURITY:
                if (event.data) {
                    if (event.data.type === 'security' && (event.data.fromComp === 'ComRestrictionsComponent' || event.data.fromComp === 'ComRestrictionsComponentGrid')) {
                        const forGrid: boolean = event.data.fromComp === 'ComRestrictionsComponentGrid' ? true : false;
                        this.resetSecuritySearch(event.data, forGrid)
                    }
                }
                break;
        }
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    get securitiesDropDown() {
        return this._securitiesDropDown;
    }

    set securitiesDropDown(secs: Array<IDropdownItem>) {
        this._securitiesDropDown = secs;
    }

    constructor(
        public ies: IrbEditorsService,
        private gls: ComRestrictionsGridLayoutService,
        private ctas: CalltoactionService,
        private util: UtilityService,
        private as: AppService
    ) {

        this.gridOptions = {
            columnDefs: this.gls.getColumnDefs(this),            
            floatingFilter: false,
            overlayNoRowsTemplate: "No portfolio-specific restrictions.",
            context: {componentParent: this},
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
        };
        
        this.frameworkComponents = this.gls.getFrameworkComponents();
    }

    populateSecurityRestrictions(searchString: string): void {
        if (searchString && searchString.length > 0) {
            const filteredItems = DropdownFilterComponent.getFilteredList(searchString,this.securities.items
              .map((security: ICustomSecurity) => ({
                    name: security.securitySymbol,
                    value: security.securityId
                })));
            this.securityRestrictionsDropdown.set(filteredItems);
        }
    }

    selectSecurityRestriction(selectedSecurity?: any): void {
        const security = this.securities.items.filter((sec: any) => sec.securityId === selectedSecurity.value)[0];
        this.selectedSecurityRestriction = security;
        this.checkSecurityRestrictionDuplicates();
    }

    clearSecurityRestriction(): void {
        delete this.selectedSecurityRestriction;
        this.checkSecurityRestriction = false;
    }

    populateCategoryRestrictionsMultiple(searchString: string): void {
        let f: Array<IDropdownItem> = [];
        let items:Array<IDropdownItem> = [];
        this.categories?.items?.forEach((category: any) => {
            items.push({name: category.custCatName,
                value: category.custCatId
            });
        });
        if(searchString && searchString.length > 0) {
            f = DropdownFilterComponent.getFilteredList(searchString,items);
        } else {
            f = [].concat(items);
        }
       this.categoryRestrictionsDropdown.set(f);
    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.onFilterChanged();
        //this.loadGrid();
        this.load();
    }

    loadGrid() {
        this.grid.api.setGridOption('rowData', this.data.portfolioRestrictions);
        this.updateRowCounts();
        this.grid.api.refreshHeader();
    }

    load() {
        const sortByKey = this.util.sortByKey;
        this.ies.getSecurities().subscribe((data: ISecurities) => {
            this.securities = data;
            
            if (this.securities) {
                sortByKey(this.securities.items, "securitySymbol", "asc");

                this.securitiesDropDown = [];
                this.securities.items.forEach(security => {
                    this.securitiesDropDown.push({
                        name: security.securitySymbol,
                        value: security.securityId
                    } as IDropdownItem);
                });
            }

            this.ies.getCustomCategories().subscribe((data: IAssetCustomCategories) => {
                if (data.items && data.items.length > 0) {
                    data.items = data.items.filter((x: any) => x.custCatId !== 0);
                }
                this.categories = data;
                sortByKey(this.categories.items, "custCatName", "asc");
            });

            this.loadGrid();
        });
    }

    updateRowCounts() {
        this.categoryRowCount = this.data && this.data.portfolioRestrictions.filter((x: any) => x.categoryId && !x.isUIdeleted).length || 0;
        this.securityRowCount = this.data && this.data.portfolioRestrictions.filter((x: any) => x.securityMasterId && !x.isUIdeleted).length || 0;
    }

    deleteAll() {
        for (let i = 0; i < this.data.portfolioRestrictions.length; i++) {
            this.data.portfolioRestrictions[i].isUIdeleted = true;
        }

        this.data.portfolioRestrictions = this.data.portfolioRestrictions.filter((x: any) => !x.isNew);
        this.loadGrid();
        this.validateInput();
    }

    delete(cc: any) {
        for (let i = 0; i < this.data.portfolioRestrictions.length; i++) {
            const a = cc.hasOwnProperty("categoryId") && this.data.portfolioRestrictions[i].categoryId === cc.categoryId && cc.categoryId;
            const b = cc.hasOwnProperty("securityMasterId") && this.data.portfolioRestrictions[i].securityMasterId === cc.securityMasterId && cc.securityMasterId;

            if((a || b) && !this.data.portfolioRestrictions[i].isUIdeleted) {
                this.data.portfolioRestrictions[i].isUIdeleted = true;
                this.data.portfolioRestrictions = this.data.portfolioRestrictions.filter((x: any) => !(x.isNew && x.isUIdeleted));
                break;
            }
        }

        this.loadGrid();
        this.validateInput();
    }

    cancelDelete() {
        this.ctas.remove();
    }

    getRowCount() {
        return this.categoryRowCount + this.securityRowCount;
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isUIdeleted;
    }

    addSecurityRestriction() {
        if (!this.checkSecurityRestrictionDuplicates()) {
            this.portfolioRestriction = {} as IPortfolioRestrictions;
            this.portfolioRestriction.securitySymbol = this.selectedSecurityRestriction.securitySymbol;
            this.portfolioRestriction.securityMasterId = this.selectedSecurityRestriction.securityId;
            this.portfolioRestriction.securityDescription = this.selectedSecurityRestriction.securityDescription;
            this.portfolioRestriction.restriction = "Never Sell";
            this.portfolioRestriction.isNew = true;
            this.data.portfolioRestrictions.push(this.portfolioRestriction);
            this.addRestriction = this.data.portfolioRestrictions.length;
            this.loadGrid();
            delete this.selectedSecurityRestriction;
            this.securityRestrictionsDropdown.inputControl.reset();
            this.securityRestrictionsDropdown.value = '';
            this.validateInput();
            this.checkSecurityRestriction = false;
        }
    }

    onAddMultipleCategoryRestrictions(categoryRestrictions: Array<IDropdownItem>) {
        if(categoryRestrictions && categoryRestrictions.length > 0) {
            categoryRestrictions.forEach((x:IDropdownItem)=>{

                //let category = x.value;
                let category = this.categories.items.filter((v: any) => {
                    return v.custCatId === x.value
                })[0];

                let alreadyAdded = this.isCategoryRestrictionDuplicate(category);
                if(!alreadyAdded && category.custCatId!== null) {
                    this.portfolioRestriction = {} as IPortfolioRestrictions;
                    this.portfolioRestriction.categoryId = category.custCatId;
                    this.portfolioRestriction.categoryLabel = category.custCatName;
                    this.portfolioRestriction.restriction = "Never Sell";
                    this.portfolioRestriction.isNew = true;
                    this.data.portfolioRestrictions.push(this.portfolioRestriction);
                    this.addRestriction = this.data.portfolioRestrictions.length;
                }
            });
            this.categoryRestrictionsDropdown?.clear();
            this.loadGrid();
            this.validateInput();
        }
    }

    checkSecurityRestrictionDuplicates() {
        let found = false;
        for (let i = 0; i < this.data.portfolioRestrictions.length; i++) {
            if (this.data.portfolioRestrictions[i].securityMasterId === this.selectedSecurityRestriction.securityId && !this.data.portfolioRestrictions[i].isUIdeleted) {
                found = true;
                break;
            }
        }
        this.enableSecurityRestrictionAddButton();
        if (found) {
            this.checkSecurityRestriction = false;
            this.checkCategoryRestriction = false;
            this.valSecDupMessage = '"' + this.selectedSecurityRestriction.securitySymbol + '"' + " is already restricted in this portfolio.<br>Portfolio cannot have more than one restriction for the same security.";
            this._valSecFlag = true;
            setTimeout(() => this.securityRestrictionTooltip.nativeElement.dispatchEvent(new Event('mouseenter')));
        } else {
            this._valSecFlag = false;
        }
        return found;
    }

    isCategoryRestrictionDuplicate(category: any) {
        let found = false;
        for (let i = 0; i < this.data.portfolioRestrictions.length; i++) {
            if (this.data.portfolioRestrictions[i].categoryId && this.data.portfolioRestrictions[i].categoryId === category.custCatId && !this.data.portfolioRestrictions[i].isUIdeleted) {
                found = true;
                break;
            }
        }
        return  found;
    }

    validateInput() {
        this.isValid = true;

        if (this.data?.portfolioRestrictions.length > 0) {
            for (let i = 0; i < this.data.portfolioRestrictions.length; i++) {
                const pfRestriction: IPortfolioRestrictions = this.data.portfolioRestrictions[i];
                if (pfRestriction.restriction === 'Never Buy' || pfRestriction.restriction === 'Never Buy and Never Sell') {
                    if (pfRestriction?.condition === 'Buy an alternate security') {
                        if (!((pfRestriction.altSecurityId !== null && pfRestriction.altSecurityId !== undefined && !Number.isNaN(pfRestriction.altSecurityId)) &&
                            (pfRestriction.altSecuritySymbol !== null && pfRestriction.altSecuritySymbol !== ''))) {
                            this.isValid = false;
                            break;
                        }
                    }
                }
            }
        }

        this.isValidChange.emit(this.isValid);
        this.change.emit(true);
    }

    enableSecurityRestrictionAddButton() {
        if (!this.selectedSecurityRestriction) {
            this.checkSecurityRestriction = false;
        } else {
            this.checkSecurityRestriction = true;
        }
    }

    getTemplate(params: any) {
        this.deleteTarget = params.data;
        return this.comRestrictionsDeleteOverlay;
    }

    @Input()
    get addRestriction(): number {
        return this._addRestriction;
    }

    set addRestriction(a: number) {
        this._addRestriction = a;
    }

    set grid(g: any) {
        this._grid = g;
    }

    get grid() {
        return this._grid;
    }

    set gridOptions(gridOpns: any) {
        this._gridOptions = gridOpns;
    }

    get gridOptions() {
        return this._gridOptions;
    }

    get securities() {
        return this._securities;
    }

    set securities(secs: ISecurities) {
        this._securities = secs;
    }

    get categoryRowCount() {
        return this._categoryRowCount;
    }

    set categoryRowCount(n: number) {
        this._categoryRowCount = n;
    }

    get securityRowCount() {
        return this._securityRowCount;
    }

    set securityRowCount(n: number) {
        this._securityRowCount = n;
    }

    get portfolioRestriction() {
        return this._portFolioRestriction;
    }

    set portfolioRestriction(pr: IPortfolioRestrictions) {
        this._portFolioRestriction = pr;
    }

    get valSecFlag() {
        return this._valSecFlag;
    }


    get categories() {
        return this._categories;
    }

    set categories(cats: IAssetCustomCategories) {
        this._categories = cats;
    }

    get category() {
        return this._category;
    }

    set category(newCat: IAssetCustomCategory) {
        this._category = newCat;
    }

    get selectedSecurityRestriction() {
        return this._selectedSecurityRestriction;
    }

    set selectedSecurityRestriction(newSecRestriction: ICustomSecurity) {
        this._selectedSecurityRestriction = newSecRestriction;
    }

    get selectedCategoryRestriction() {
        return this._selectedCategoryRestriction;
    }

    set selectedCategoryRestriction(newCCRestriction: IAssetCustomCategory) {
        this._selectedCategoryRestriction = newCCRestriction;
    }

    resetCategorySearch(dataParam: any) {
        if (dataParam && dataParam.newItems && dataParam.newItems.length !== 0) {
            this.categories.items = this.categories.items.concat(dataParam.newItems);
            const sortByKey = this.util.sortByKey;
            sortByKey(this.categories.items, "custCatName", "asc");

            if (dataParam.newItemsForDD && dataParam.newItemsForDD.length !== 0) {
                this.categoryRestrictionsDropdown.setValue(dataParam.newItemsForDD);
            }
            if (this.categoryRestrictionsDropdown.display && this.categoryRestrictionsDropdown.display.nativeElement) {
                this.categoryRestrictionsDropdown.display.nativeElement.click();
            }

            this.ies.getCustomCategories().subscribe((data: IAssetCustomCategories) => {
                this.categories = data;
                const sortByKey = this.util.sortByKey;
                sortByKey(this.categories.items, "custCatName", "asc");
            });
        }
    }

    resetSecuritySearch(dataParam: any, forGrid: boolean) {
        if (dataParam && dataParam.newItemsForDD && dataParam.newItemsForDD.length !== 0) {
            let newItem: any;
            this.securities = this.securities ? this.securities : {
                items: [],
                identifier: 'securityId',
                label: 'securitySymbol'
            };
            dataParam.securities.responseData.forEach((newSec: any) => {
                newItem = {
                    restriction: "No Restriction",
                    securityDescription: newSec.securityDescription,
                    securityId: newSec.securityId,
                    securitySymbol: newSec.securitySymbol,
                    securityType: newSec.securityType,
                    isFollowingCatSuggestion: newSec.isFollowingCatSuggestion,
                    versionId: newSec.versionId
                };
                this.securities.items.push(newItem);
            });
            this.securitiesDropDown = this.securitiesDropDown.concat(dataParam.newItemsForDD);

            this.ies.getSecurities().subscribe((listOfSecurities: ISecurities) => {
                this.securities = listOfSecurities;
                if (this.securities) {
                    const sortByKey = this.util.sortByKey;
                    sortByKey(this.securities.items, "securitySymbol", "asc");

                    this.securitiesDropDown = [];
                    this.securities.items.forEach(security => {
                        this.securitiesDropDown.push({
                            name: security.securitySymbol,
                            value: security.securityId
                        } as IDropdownItem);
                    });
                }
            });
            if (!forGrid) {
                this.securityRestrictionsDropdown.inputControl.setValue(dataParam.dropdownSearchValue);
                if (dataParam.newItemsForDD && dataParam.newItemsForDD.length !== 0) {
                    this.selectSecurityRestriction(dataParam.newItemsForDD[0]);
                }
            } else {
                const rowIndex: number = dataParam.rowIndex;
                if (rowIndex !== null || typeof rowIndex !== "undefined") {
                    this.grid.api.forEachNode((node: any) => {
                        if (node.rowIndex === rowIndex) {
                            node.data.altSecuritySymbol = dataParam.newItemsForDD[0].name;
                            node.data.altSecurityId = dataParam.newItemsForDD[0].value;
                        }
                    });
                    const n = this.grid.api.getDisplayedRowAtIndex(rowIndex);
                    this.grid.api.redrawRows({rowNodes: [n]});
                    this.validateInput();
                }
            }
        }
    }

    openNewItemCategoryFlow(dataParam: any) {
        dataParam = Object.assign({}, this.addNewCategoryItem, dataParam);
        this.as.setToLocal("LAST_TAB_INDEX", 0);
        this.as.fireEvent(IrbSettingsEvents.SHOW, dataParam);
    }

    openNewItemSecurityFlow(dataParam: any, forGrid: boolean) {
        if (!forGrid) {
            dataParam = Object.assign({}, this.addNewSecurityItem, dataParam);
        }
        this.as.fireEvent(IrbModelsEvents.SHOW_CREATE_SECURITIES, dataParam);
    }
}
